import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { allGuides } from './guides';
import { api, getAccessToken } from '../api/client';
import { Guide } from '../ui/Guide';
import { showToast } from '../utils/toast';

// ─── API helpers ─────────────────────────────────────────────────────────────
// (The hand-built API_BASE is gone with the hand-built headers: api() owns the
// base URL, and a second copy of it is a second thing to get wrong.)

// THE TOKEN IS NOT IN localStorage, AND NEVER HAS BEEN (2026-10-03). The access
// token lives in sessionStorage under 'aidey_at_<slot>' and is held in memory by
// api/client.js — see setAccessToken. Reading localStorage('token') or
// sessionStorage('token') returns null for every user who has ever signed in, so
// every request below went out as `Bearer null`, and the gate at the bottom of
// this file ("no token yet — keep polling") never opened. The guided tours have
// never run for anybody.
//
// Nothing here guesses at storage any more: it asks the one module that owns the
// token, and goes through api() so an expired token is refreshed rather than
// silently failing.
function getToken() {
  return getAccessToken();
}

async function fetchProgress() {
  const res = await api('/api/tours/progress');
  const data = await res.json();
  return data.progress || {};
}

async function saveProgress(tourId, status) {
  await api('/api/tours/progress', {
    method: 'POST',
    body: JSON.stringify({ tourId, status, currentStep: 0 }),
  }).catch(err => console.warn('[tours] saveProgress failed:', err));
}

// ─── Context ──────────────────────────────────────────────────────────────────
const TourContext = createContext(null);

export function TourProvider({ children }) {
  // progressMap: { [tourId]: { status, currentStep, completedAt } }
  // status values: 'in_progress' | 'skipped_once' | 'skipped' | 'completed'
  const [progressMap, setProgressMap] = useState({});
  const [loaded, setLoaded]           = useState(false);
  const [guideOpen, setGuideOpen]     = useState(null); // tourId | null

  // Ref kept in sync with progressMap so openGuide can read current status
  // without progressMap in its dep array (avoids recreating on every status change).
  const progressMapRef = useRef(progressMap);
  useEffect(() => { progressMapRef.current = progressMap; }, [progressMap]);

  // Snapshot of the status a guide had before it was opened this session.
  // Used by closeGuide to decide which final status to write.
  const preOpenStatusRef = useRef(null);

  // Load tour progress once we have an auth token.
  // TourProvider mounts before the user is authenticated, so we poll for a
  // token rather than fetching immediately (which would 401 and incorrectly
  // set loaded=true with an empty map, firing every guide on every refresh).
  useEffect(() => {
    let cancelled = false;

    function attempt() {
      if (!getToken()) return false; // no token yet, keep polling
      fetchProgress()
        .then(map => {
          if (!cancelled) { progressMapRef.current = map; setProgressMap(map); setLoaded(true); }
        })
        .catch(() => {}); // on failure keep guides dormant; interval already cleared
      return true; // had a token; stop polling regardless of fetch result
    }

    if (!attempt()) {
      const iv = setInterval(() => { if (attempt()) clearInterval(iv); }, 500);
      return () => { cancelled = true; clearInterval(iv); };
    }
    return () => { cancelled = true; };
  }, []);

  // hasSeenTour: controls auto-fire gate only.
  // Returns true (suppress auto-fire) only for terminal statuses.
  // skipped_once, in_progress, and no record all allow auto-fire.
  const hasSeenTour = useCallback(
    tourId => {
      const s = progressMap[tourId]?.status;
      return s === 'completed' || s === 'skipped';
    },
    [progressMap]
  );

  // openGuide: shows the guide for tourId.
  // Auto-fire passes no options; HelpButton passes { force: true } to open
  // regardless of status (so ? always works).
  const openGuide = useCallback(
    (tourId, { force = false } = {}) => {
      if (!allGuides[tourId]) return;
      if (!force && hasSeenTour(tourId)) return;

      const currentStatus = progressMapRef.current[tourId]?.status ?? null;
      preOpenStatusRef.current = currentStatus;

      // Write in_progress to prevent a mid-guide page-refresh from re-firing
      // the guide. Skip if already in a terminal state (force-opening a completed
      // or permanently skipped guide should not downgrade its status).
      if (currentStatus !== 'completed' && currentStatus !== 'skipped') {
        saveProgress(tourId, 'in_progress');
        setProgressMap(prev => ({
          ...prev,
          [tourId]: { ...prev[tourId], status: 'in_progress' },
        }));
      }

      setGuideOpen(tourId);
    },
    [hasSeenTour]
  );

  // closeGuide: Block M (UI-23), on the shared spotlight guide.
  //   dismissed (Escape, Skip tour, the close button) → the two-strike rule:
  //   • first real dismiss (null / not_started)       → skipped_once
  //   • second real dismiss (skipped_once / in_prog)  → skipped (permanent)
  //   • force-opened a terminal guide then dismissed  → restore original (no write)
  //   finished (Got it on the last step):
  //   • "Don't show again" ticked                     → completed (never opens by itself again)
  //   • not ticked, first time (null / not_started)   → skipped_once (one more showing, later)
  //   • not ticked, again (skipped_once / in_prog)    → completed (seen twice: retired)
  //   • force-opened a terminal guide                 → unchanged, unless ticked (→ completed)
  // Stored per person on the server (user_tour_progress), so it holds on every device.
  const closeGuide = useCallback(
    ({ finished = false, dontShowAgain = false } = {}) => {
      const tourId = guideOpen;
      if (!tourId) return;

      const pre = preOpenStatusRef.current;
      const terminal = pre === 'completed' || pre === 'skipped';
      let status;

      if (finished && dontShowAgain) {
        status = 'completed';
      } else if (terminal) {
        // Guide was force-opened via ?: restore original, no change
        status = pre;
      } else if (pre === 'skipped_once' || pre === 'in_progress') {
        // Second time round (or an interrupted guide explicitly closed): retire it
        status = finished ? 'completed' : 'skipped';
      } else {
        // First time (no record, not_started, null): one more chance
        status = 'skipped_once';
      }

      if (status !== pre) {
        saveProgress(tourId, status);
      }
      setProgressMap(prev => ({
        ...prev,
        [tourId]: { status, currentStep: 0 },
      }));
      setGuideOpen(null);
      // The mockup's note: closing early says where the guide lives now.
      if (!finished) showToast('Guide closed. Tap ? next to the page title to see it again.', { type: 'info', ms: 3500 });
    },
    [guideOpen]
  );

  // INTRO-2: record that a screen's first-use chat intro has fired. Stored under a
  // namespaced chat:<screen> id in the SAME user_tour_progress store (no new table).
  // The intro cap is derived from how many chat:* records exist, so this needs no
  // separate counter. Updates the local map synchronously so a re-render cannot
  // double-fire before the save round-trips.
  const recordChatIntro = useCallback((screenId) => {
    const key = `chat:${screenId}`;
    if (progressMapRef.current[key]) return;
    saveProgress(key, 'completed');
    setProgressMap(prev => ({ ...prev, [key]: { status: 'completed', currentStep: 0 } }));
  }, []);

  const currentSteps = guideOpen ? (allGuides[guideOpen]?.steps || []) : [];

  return (
    <TourContext.Provider value={{ openGuide, hasSeenTour, loaded, progress: progressMap, isGuideOpen: guideOpen !== null, recordChatIntro }}>
      {children}
      <Guide
        steps={currentSteps}
        open={guideOpen !== null}
        onClose={() => closeGuide()}
        onFinish={({ dontShowAgain }) => closeGuide({ finished: true, dontShowAgain })}
      />
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside <TourProvider>');
  return ctx;
}
